import { T } from "@tldraw/validate"
import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  defaultColorNames,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultHorizontalAlignStyle,
  DefaultSizeStyle,
  DefaultVerticalAlignStyle,
  RecordProps,
  richTextValidator,
  StyleProp,
  TLBaseShape,
  TLDefaultColorStyle,
  TLDefaultDashStyle,
  TLDefaultFillStyle,
  TLDefaultFontStyle,
  TLDefaultHorizontalAlignStyle,
  TLDefaultSizeStyle,
  TLDefaultVerticalAlignStyle,
  TLPageId,
  TLRichText,
  toRichText,
} from "tldraw"
import { Color, Expr } from "./Expr"
import { VisibilityAndOpacity } from "./CustomDrawShape"

export const DefaultLabelColorStyle = StyleProp.defineEnum(
  "tldraw:labelColor2",
  {
    defaultValue: "black",
    values: defaultColorNames,
  },
)

/** @public */
export const GeoShapeGeoStyle = StyleProp.defineEnum("tldraw:custom-geo", {
  defaultValue: "rectangle",
  values: [
    "cloud",
    "rectangle",
    "ellipse",
    "triangle",
    "diamond",
    "pentagon",
    "hexagon",
    "octagon",
    "star",
    "rhombus",
    "rhombus-2",
    "oval",
    "trapezoid",
    "arrow-right",
    "arrow-left",
    "arrow-up",
    "arrow-down",
    "x-box",
    "check-box",
    "heart",
  ],
})

/** @public */
export type LDGeoShapeGeoStyle = T.TypeOf<typeof GeoShapeGeoStyle>

/** @public */
export type LDGeoShapeProps = {
  geo: LDGeoShapeGeoStyle
  dash: TLDefaultDashStyle
  url: string
  w: number
  h: number
  growY: number
  scale: number
  layer: string
  pageId: TLPageId
  fill: boolean

  // Text properties
  labelColor: TLDefaultColorStyle

  font: TLDefaultFontStyle
  align: TLDefaultHorizontalAlignStyle
  verticalAlign: TLDefaultVerticalAlignStyle
  richText: TLRichText
} & (
  | {
      fill: false
      parameterValues: VisibilityAndOpacity & {
        strokeColor: Expr<Color>
        strokeWidth: Expr<number>
        roundness: Expr<number>
      }
    }
  | {
      fill: true
      parameterValues: VisibilityAndOpacity & {
        fillColor: Expr<Color>
        roundness: Expr<number>
      }
    }
)

/** @public */
export type LDGeoShape = TLBaseShape<"custom-geo", LDGeoShapeProps>

/** @public */
export const geoShapeProps: RecordProps<LDGeoShape> = {
  geo: GeoShapeGeoStyle,
  dash: DefaultDashStyle,
  url: T.linkUrl,
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,
  growY: T.positiveNumber,
  scale: T.nonZeroNumber,
  layer: T.string,
  pageId: T.any,

  // Text properties
  labelColor: DefaultLabelColorStyle,
  fill: T.boolean,
  font: DefaultFontStyle,
  align: DefaultHorizontalAlignStyle,
  verticalAlign: DefaultVerticalAlignStyle,
  richText: richTextValidator,
  parameterValues: T.any,
}

const geoShapeVersions = createShapePropsMigrationIds("custom-geo", {
  AddUrlProp: 1,
  AddLabelColor: 2,
  RemoveJustify: 3,
  AddCheckBox: 4,
  AddVerticalAlign: 5,
  MigrateLegacyAlign: 6,
  AddCloud: 7,
  MakeUrlsValid: 8,
  AddScale: 9,
  AddRichText: 10,
})

export { geoShapeVersions as geoShapeVersions }

/** @public */
export const geoShapeMigrations = createShapePropsMigrationSequence({
  sequence: [
    {
      id: geoShapeVersions.AddUrlProp,
      up: (props) => {
        props.url = ""
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.AddLabelColor,
      up: (props) => {
        props.labelColor = "black"
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.RemoveJustify,
      up: (props) => {
        if (props.align === "justify") {
          props.align = "start"
        }
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.AddCheckBox,
      up: (_props) => {
        // noop
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.AddVerticalAlign,
      up: (props) => {
        props.verticalAlign = "middle"
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.MigrateLegacyAlign,
      up: (props) => {
        let newAlign: TLDefaultHorizontalAlignStyle
        switch (props.align) {
          case "start":
            newAlign = "start-legacy"
            break
          case "end":
            newAlign = "end-legacy"
            break
          default:
            newAlign = "middle-legacy"
            break
        }
        props.align = newAlign
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.AddCloud,
      up: (_props) => {
        // noop
      },
      down: "retired",
    },
    {
      id: geoShapeVersions.MakeUrlsValid,
      up: (props) => {
        if (!T.linkUrl.isValid(props.url)) {
          props.url = ""
        }
      },
      down: (_props) => {
        // noop
      },
    },
    {
      id: geoShapeVersions.AddScale,
      up: (props) => {
        props.scale = 1
      },
      down: (props) => {
        delete props.scale
      },
    },
    {
      id: geoShapeVersions.AddRichText,
      up: (props) => {
        props.richText = toRichText(props.text)
        delete props.text
      },
      // N.B. Explicitly no down state so that we force clients to update.
      // down: (props) => {
      // 	delete props.richText
      // },
    },
  ],
})
